import { useCallback, useEffect, useRef, useState } from 'react';
import type { FleetJobView } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';

const POLL_INTERVAL_MS = 10_000;

type Options = {
  /** Suppresses the interval while a mutation is in flight (BL-108). */
  readonly paused?: boolean;
};

type Result = {
  readonly jobs: ReadonlyArray<FleetJobView>;
  readonly refresh: () => Promise<void>;
};

/**
 * The fleet's live jobs for the machines page (fleet-agent ADR 0012) — what
 * puts a lane's heartbeat, progress line and suspect flag beside it, and the
 * Suspect badge on its machine's tile. Its own 10s poll beside the overview's,
 * suppressed while the tab is hidden, a mutation is in flight, or a fetch is
 * already out. A failed read keeps the last answer: losing the pulse for one
 * poll must not blank a Suspect badge the operator is looking at.
 */
export function useFleetJobs(options?: Options): Result {
  const [jobs, setJobs] = useState<ReadonlyArray<FleetJobView>>([]);
  const inFlightRef = useRef(false);
  const pausedRef = useRef(options?.paused ?? false);
  pausedRef.current = options?.paused ?? false;

  const load = useCallback(async (loadOptions?: { readonly force?: boolean }) => {
    if (inFlightRef.current && loadOptions?.force !== true) return;
    inFlightRef.current = true;

    try {
      const result = await apiClient.adminFleetJobs.list({ query: { state: 'all' } });

      if (result.ok) setJobs(result.body.data);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    void load({ force: true });

    const interval = window.setInterval(() => {
      if (pausedRef.current) return;

      if (document.visibilityState !== 'visible') return;

      void load();
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [load]);

  const refresh = useCallback(() => load({ force: true }), [load]);

  return { jobs, refresh };
}
